/**
 * Co-marketing panel: the right-hand card of the Marketing tab. The partner
 * summary, then the open tasks, then the notes history.
 *
 * Differences from the design, kept on purpose:
 * - Following on Instagram is a three-way select (Unknown, Yes, No), not a
 *   checkbox. A checkbox cannot say "unknown" and would turn it into "no".
 * - Someone who cannot edit sees a static done/open marker on action items, not
 *   a disabled checkbox.
 * - An empty open list always reads "No open tasks."; the design's
 *   "Nothing yet" wording needs the totals of both lists.
 * - Failures from ticking, editing or deleting a note show in a dismissible
 *   alert above the list, because a refresh can remove the note that would have
 *   carried the message.
 * - On a wide screen the history scrolls inside a box of its own, as in the
 *   design; on a narrow screen the page scrolls instead.
 */
import { Lock } from 'lucide-react';

import type { VenueDetail } from '../../../../api/venues';

import { CoMarketingSummary } from './CoMarketingSummary';
import { MarketingNoteList } from './MarketingNoteList';

/** How tall the history gets on a wide screen before it scrolls. */
const HISTORY_MAX_HEIGHT = 360;

interface CoMarketingPanelProps {
  venue: VenueDetail;
  /** The columns are stacked, so the page scrolls rather than the history. */
  stacked: boolean;
}

export function CoMarketingPanel({ venue, stacked }: CoMarketingPanelProps) {
  const canWrite = venue.canWriteMarketing;
  const shared = {
    venueId: venue.id,
    canWrite,
    canEditMarketingOnly: !venue.canEdit,
    regionName: venue.region?.name ?? null,
  };
  return (
    <section
      className="nb-card"
      aria-label="Co-marketing"
      style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}
    >
      <div className="nb-card-hd" style={{ marginBottom: -6 }}>
        <h3>Co-marketing</h3>
        {!canWrite && (
          <span
            className="nb-pill"
            style={{
              height: 20,
              fontSize: 10,
              padding: '0 8px',
              gap: 5,
              fontWeight: 700,
              background: 'var(--nb-surface-2)',
              color: 'var(--nb-muted)',
              borderColor: 'transparent',
            }}
          >
            <Lock size={11} aria-hidden="true" />
            View only
          </span>
        )}
      </div>
      <CoMarketingSummary venue={venue} canEdit={canWrite} />
      <MarketingNoteList {...shared} list="open" />
      <MarketingNoteList
        {...shared}
        list="history"
        maxHeight={stacked ? undefined : HISTORY_MAX_HEIGHT}
      />
    </section>
  );
}
